import { MouseEvent, PointerEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clamp } from '@util/number';
import { useDerivedState } from '@hooks/useDerivedState';

type ColumnsResizeProps = {
    initialWidths: (number | null)[];
    onWidthChange: (i: number, width: number) => void;
    onWidthReset?: (i: number) => void;
    position?: 'left' | 'right';
    minWidth?: number;
    maxWidth?: number;
};

export const useColumnsResize = ({
    initialWidths,
    onWidthChange,
    onWidthReset,
    position = 'left',
    minWidth = 0,
    maxWidth = 1e5,
}: ColumnsResizeProps) => {
    const [widths, setWidths] = useDerivedState(
        initialWidths.map((w) => w ?? minWidth),
        [initialWidths]
    );
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

    // Use refs to avoid rebinding mouse handlers on every move
    const anchorRef = useRef<number>(0);
    const draggingIndexRef = useRef<number | null>(null);
    const widthsRef = useRef<number[]>(widths);

    draggingIndexRef.current = draggingIndex;
    widthsRef.current = widths;

    const resizeHandlers = useMemo(() => {
        return initialWidths.map((_, i) => {
            const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
                const { current: widths } = widthsRef;
                const width = widths[i];

                e.preventDefault();
                e.stopPropagation();

                // Track click anchor relative to 0
                const sign = position == 'right' ? -1 : 1;
                anchorRef.current = sign * e.clientX - width;
                setDraggingIndex((draggingIndexRef.current = i));

                (e.target as Element)?.setPointerCapture(e.pointerId);
            };

            const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
                const { current: anchor } = anchorRef;
                const { current: draggingIndex } = draggingIndexRef;
                const { current: widths } = widthsRef;

                if (draggingIndex == null) return;
                e.stopPropagation();

                const sign = position == 'right' ? -1 : 1;
                const width = clamp(Math.round(sign * e.clientX - anchor), minWidth, maxWidth);

                const ws = [...widths];
                ws[i] = width;

                setWidths((widthsRef.current = ws));
            };

            const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
                const { current: widths } = widthsRef;
                const width = widths[i];

                e.stopPropagation();
                if (width != initialWidths[i]) onWidthChange(i, width);
                setDraggingIndex((draggingIndexRef.current = null));

                (e.target as Element)?.releasePointerCapture(e.pointerId);
            };

            const onDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                onWidthReset?.(i);
            };

            return { onPointerDown, onPointerMove, onPointerUp, onDoubleClick };
        });
    }, [initialWidths, setWidths, onWidthChange, onWidthReset, minWidth, maxWidth, position]);

    useLayoutEffect(() => {
        if (draggingIndex == null) return;

        // Add workaround for chrome bug - macOS pointerUp while moving mouse does not fire when pointer capture is active
        // https://bugs.chromium.org/p/chromium/issues/detail?id=1459632&q=pointerup%20event&can=2
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        document.addEventListener('pointerup', resizeHandlers[draggingIndex].onPointerUp as any);
        return () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            document.removeEventListener('pointerup', resizeHandlers[draggingIndex].onPointerUp as any);
        };
    }, [draggingIndex, resizeHandlers]);

    return { widths, draggingIndex, resizeHandlers };
};
